import type { CountryCode } from "libphonenumber-js";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { z } from "zod/v4";

import { isWallClock } from "./authored-time";
import { formUploadUrlInput } from "./form";
import { richTextDocSchema } from "./rich-text";

export * from "./audit";
export * from "./authored-time";
export * from "./borne";
export * from "./form";
export * from "./guest-status";
export * from "./invite-search";
export * from "./location";
export * from "./amenity";
export * from "./parcel";
export * from "./pass-status";
export * from "./day-end";
export * from "./expected-day";
export * from "./expected-guests";
export * from "./next-visit-start";
export * from "./push-channel";
export * from "./review-queue";
export * from "./rich-text";
export * from "./report-period";
export * from "./self-fill";
export * from "./spot-arrival";
export * from "./stale-tzdata";
export * from "./entry-reason";
export * from "./spot-arrival-display";
export * from "./support";
export * from "./terms";
export * from "./unit";
export * from "./visit-time";
export * from "./weekday";
export * from "./visitor";

/**
 * Shared zod schemas live here. This package is intentionally minimal in the
 * boilerplate — add cross-cutting validators (shared between api, web, and
 * mobile) as your app grows.
 */
export const paginationInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});
export type PaginationInput = z.infer<typeof paginationInput>;

export type { CountryCode };
export { getCountryCallingCode };

/**
 * Parse a user-entered phone number to canonical E.164, or null if invalid.
 *
 * Canonical E.164 is load-bearing: Better-Auth's phone-number plugin matches
 * users by EXACT string equality on `phoneNumber` during OTP verify, so every
 * path that persists `user.phoneNumber` MUST go through here first.
 *
 * `defaultCountry` only applies to bare local input typed without a `+`
 * (so a Moroccan typing "612345678" still resolves to +212…). Explicit
 * international input (e.g. "+33…") is honored regardless of the default.
 *
 *   parsePhone("0612345678")        → "+212612345678"
 *   parsePhone("612345678", "FR")   → "+33612345678"
 *   parsePhone("+15551234567")      → null  (valid-length but unassignable 555 exchange)
 *   parsePhone("+2126282222222222") → null
 */
export function parsePhone(
  input: string,
  defaultCountry: CountryCode = "MA",
): string | null {
  const parsed = parsePhoneNumberFromString(input, defaultCountry);
  if (!parsed?.isValid()) return null;
  return parsed.number; // E.164, e.g. "+212612345678"
}

/** Client-side validity check; shares the exact rule used server-side. */
export function isValidPhone(
  input: string,
  defaultCountry: CountryCode = "MA",
): boolean {
  return parsePhone(input, defaultCountry) !== null;
}

/**
 * Single source of truth for phone input on every write path: trims, validates,
 * and transforms to canonical E.164. Defaults the region to Morocco, so a value
 * already in E.164 (what the clients send) passes through idempotently.
 */
export const phoneSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const e164 = parsePhone(v);
    if (e164 === null) {
      ctx.addIssue({
        code: "custom",
        message: "Numéro de téléphone invalide.",
      });
      return z.NEVER;
    }
    return e164;
  });

/**
 * Inverse of `parsePhone`: turns canonical E.164 into the `{ country, national }`
 * pair the web invite form's `GuestRow` holds (a local-format string plus its
 * region). Returns null for anything not already valid E.164 — callers should
 * run `parsePhone` first.
 */
export function splitE164(
  e164: string,
): { country: CountryCode; national: string } | null {
  // Strict E.164 format: + followed by 1-15 digits, no separators or spaces.
  if (!/^\+[1-9]\d{1,14}$/.test(e164)) return null;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed?.isValid() || !parsed.country) return null;
  return {
    country: parsed.country,
    national: parsed.formatNational().replace(/[^\d]/g, ""),
  };
}

/**
 * Pick which organization a session with no active org should default to.
 * Returns the caller's most-recently-joined membership (latest `createdAt`
 * wins), or null when they hold none.
 *
 * Auto-promotion used to fire only for sole-membership users; multi-org
 * owners/syndics were deliberately left with a null active org so they'd land
 * on a picker. But only the Home route handles a null active org — every other
 * dashboard route is `adminProcedure`/`memberProcedure`, which 403s with
 * "No active organization" — so a multi-org user who clicked any sidebar item
 * hit a dead end. Defaulting to their latest membership avoids that; the
 * account-menu switcher still lets them change résidence afterwards.
 *
 * Order-independent by design (it scans for the max) so callers don't have to
 * `ORDER BY` in SQL and can't be broken by an unordered query.
 */
export function pickActiveOrgId(
  memberships: readonly { orgId: string; createdAt: Date }[],
): string | null {
  let latest: { orgId: string; createdAt: Date } | null = null;
  for (const m of memberships) {
    if (!latest || m.createdAt.getTime() > latest.createdAt.getTime()) {
      latest = m;
    }
  }
  return latest?.orgId ?? null;
}

/** ISO 3166-1 alpha-2 → regional-indicator flag emoji ("MA" → "🇲🇦"). */
export function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export interface CountryOption {
  iso: CountryCode;
  /** Calling code without the leading "+", e.g. "212". */
  dialCode: string;
  flag: string;
  /** French display name (bundled — see COUNTRY_NAMES_FR). */
  name: string;
}

/**
 * French country names keyed by ISO 3166-1 alpha-2, bundled rather than read
 * from `Intl.DisplayNames`. The mobile app runs on a Hermes build without ICU,
 * where `Intl.DisplayNames` is absent — so relying on it left the picker
 * showing bare ISO codes ("MA", "FR") instead of names. Bundling makes names
 * deterministic on every platform (web included). Generated from
 * `Intl.DisplayNames(["fr"], { type: "region" })` over `getCountries()`.
 */
export const COUNTRY_NAMES_FR: Record<string, string> = {
  AC: "Île de l’Ascension",
  AD: "Andorre",
  AE: "Émirats arabes unis",
  AF: "Afghanistan",
  AG: "Antigua-et-Barbuda",
  AI: "Anguilla",
  AL: "Albanie",
  AM: "Arménie",
  AO: "Angola",
  AR: "Argentine",
  AS: "Samoa américaines",
  AT: "Autriche",
  AU: "Australie",
  AW: "Aruba",
  AX: "Îles Åland",
  AZ: "Azerbaïdjan",
  BA: "Bosnie-Herzégovine",
  BB: "Barbade",
  BD: "Bangladesh",
  BE: "Belgique",
  BF: "Burkina Faso",
  BG: "Bulgarie",
  BH: "Bahreïn",
  BI: "Burundi",
  BJ: "Bénin",
  BL: "Saint-Barthélemy",
  BM: "Bermudes",
  BN: "Brunei",
  BO: "Bolivie",
  BQ: "Pays-Bas caribéens",
  BR: "Brésil",
  BS: "Bahamas",
  BT: "Bhoutan",
  BW: "Botswana",
  BY: "Biélorussie",
  BZ: "Belize",
  CA: "Canada",
  CC: "Îles Cocos",
  CD: "Congo-Kinshasa",
  CF: "République centrafricaine",
  CG: "Congo-Brazzaville",
  CH: "Suisse",
  CI: "Côte d’Ivoire",
  CK: "Îles Cook",
  CL: "Chili",
  CM: "Cameroun",
  CN: "Chine",
  CO: "Colombie",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cap-Vert",
  CW: "Curaçao",
  CX: "Île Christmas",
  CY: "Chypre",
  CZ: "Tchéquie",
  DE: "Allemagne",
  DJ: "Djibouti",
  DK: "Danemark",
  DM: "Dominique",
  DO: "République dominicaine",
  DZ: "Algérie",
  EC: "Équateur",
  EE: "Estonie",
  EG: "Égypte",
  EH: "Sahara occidental",
  ER: "Érythrée",
  ES: "Espagne",
  ET: "Éthiopie",
  FI: "Finlande",
  FJ: "Fidji",
  FK: "Îles Malouines",
  FM: "Micronésie",
  FO: "Îles Féroé",
  FR: "France",
  GA: "Gabon",
  GB: "Royaume-Uni",
  GD: "Grenade",
  GE: "Géorgie",
  GF: "Guyane française",
  GG: "Guernesey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Groenland",
  GM: "Gambie",
  GN: "Guinée",
  GP: "Guadeloupe",
  GQ: "Guinée équatoriale",
  GR: "Grèce",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guinée-Bissau",
  GY: "Guyana",
  HK: "R.A.S. chinoise de Hong Kong",
  HN: "Honduras",
  HR: "Croatie",
  HT: "Haïti",
  HU: "Hongrie",
  ID: "Indonésie",
  IE: "Irlande",
  IL: "Israël",
  IM: "Île de Man",
  IN: "Inde",
  IO: "Territoire britannique de l’océan Indien",
  IQ: "Irak",
  IR: "Iran",
  IS: "Islande",
  IT: "Italie",
  JE: "Jersey",
  JM: "Jamaïque",
  JO: "Jordanie",
  JP: "Japon",
  KE: "Kenya",
  KG: "Kirghizstan",
  KH: "Cambodge",
  KI: "Kiribati",
  KM: "Comores",
  KN: "Saint-Christophe-et-Niévès",
  KP: "Corée du Nord",
  KR: "Corée du Sud",
  KW: "Koweït",
  KY: "Îles Caïmans",
  KZ: "Kazakhstan",
  LA: "Laos",
  LB: "Liban",
  LC: "Sainte-Lucie",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lituanie",
  LU: "Luxembourg",
  LV: "Lettonie",
  LY: "Libye",
  MA: "Maroc",
  MC: "Monaco",
  MD: "Moldavie",
  ME: "Monténégro",
  MF: "Saint-Martin",
  MG: "Madagascar",
  MH: "Îles Marshall",
  MK: "Macédoine du Nord",
  ML: "Mali",
  MM: "Myanmar (Birmanie)",
  MN: "Mongolie",
  MO: "R.A.S. chinoise de Macao",
  MP: "Îles Mariannes du Nord",
  MQ: "Martinique",
  MR: "Mauritanie",
  MS: "Montserrat",
  MT: "Malte",
  MU: "Maurice",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexique",
  MY: "Malaisie",
  MZ: "Mozambique",
  NA: "Namibie",
  NC: "Nouvelle-Calédonie",
  NE: "Niger",
  NF: "Île Norfolk",
  NG: "Nigeria",
  NI: "Nicaragua",
  NL: "Pays-Bas",
  NO: "Norvège",
  NP: "Népal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "Nouvelle-Zélande",
  OM: "Oman",
  PA: "Panama",
  PE: "Pérou",
  PF: "Polynésie française",
  PG: "Papouasie-Nouvelle-Guinée",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Pologne",
  PM: "Saint-Pierre-et-Miquelon",
  PR: "Porto Rico",
  PS: "Territoires palestiniens",
  PT: "Portugal",
  PW: "Palaos",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "La Réunion",
  RO: "Roumanie",
  RS: "Serbie",
  RU: "Russie",
  RW: "Rwanda",
  SA: "Arabie saoudite",
  SB: "Îles Salomon",
  SC: "Seychelles",
  SD: "Soudan",
  SE: "Suède",
  SG: "Singapour",
  SH: "Sainte-Hélène",
  SI: "Slovénie",
  SJ: "Svalbard et Jan Mayen",
  SK: "Slovaquie",
  SL: "Sierra Leone",
  SM: "Saint-Marin",
  SN: "Sénégal",
  SO: "Somalie",
  SR: "Suriname",
  SS: "Soudan du Sud",
  ST: "Sao Tomé-et-Principe",
  SV: "Salvador",
  SX: "Saint-Martin (partie néerlandaise)",
  SY: "Syrie",
  SZ: "Eswatini",
  TA: "Tristan da Cunha",
  TC: "Îles Turques-et-Caïques",
  TD: "Tchad",
  TG: "Togo",
  TH: "Thaïlande",
  TJ: "Tadjikistan",
  TK: "Tokelau",
  TL: "Timor oriental",
  TM: "Turkménistan",
  TN: "Tunisie",
  TO: "Tonga",
  TR: "Turquie",
  TT: "Trinité-et-Tobago",
  TV: "Tuvalu",
  TW: "Taïwan",
  TZ: "Tanzanie",
  UA: "Ukraine",
  UG: "Ouganda",
  US: "États-Unis",
  UY: "Uruguay",
  UZ: "Ouzbékistan",
  VA: "État de la Cité du Vatican",
  VC: "Saint-Vincent-et-les Grenadines",
  VE: "Venezuela",
  VG: "Îles Vierges britanniques",
  VI: "Îles Vierges des États-Unis",
  VN: "Viêt Nam",
  VU: "Vanuatu",
  WF: "Wallis-et-Futuna",
  WS: "Samoa",
  XK: "Kosovo",
  YE: "Yémen",
  YT: "Mayotte",
  ZA: "Afrique du Sud",
  ZM: "Zambie",
  ZW: "Zimbabwe",
};

/**
 * All selectable countries for the phone-number picker, sorted with Morocco
 * pinned first then by French name. Names are bundled (COUNTRY_NAMES_FR), so
 * the picker shows real names on every platform — including the mobile Hermes
 * build that lacks `Intl.DisplayNames`. `flag` is the emoji flag; it renders in
 * browsers but not on platforms whose system font lacks regional-indicator
 * glyphs (e.g. many Android builds), so the mobile picker shows an ISO-code
 * badge instead and ignores this field.
 */
export function buildCountryOptions(): CountryOption[] {
  const options = getCountries().map((iso) => ({
    iso,
    dialCode: getCountryCallingCode(iso),
    flag: flagEmoji(iso),
    name: COUNTRY_NAMES_FR[iso] ?? iso,
  }));
  options.sort((a, b) => {
    if (a.iso === "MA") return -1;
    if (b.iso === "MA") return 1;
    return a.name.localeCompare(b.name, "fr");
  });
  return options;
}

/**
 * Gately member roles within a résidence (organization).
 *   - syndic       — résidence admin; the only role that can create accounts.
 *   - proprietaire — homeowner / resident.
 *   - gardien      — security guard at the gate.
 *   - locataire    — tenant occupying a unit under "mise en location" (the
 *                    owner declares them via occupancyDeclareInput). Distinct
 *                    from the `locataire` VISITOR TYPE in `visitorTypes`.
 *
 * NOTE: `owner` (Gately platform staff, who provisions résidences via
 * `organization.create`) is intentionally NOT in this enum — it is not an
 * assignable role in the admin "create account" flow.
 */
export const gatelyRoles = [
  "syndic",
  "proprietaire",
  "gardien",
  "locataire",
] as const;
export const gatelyRoleSchema = z.enum(gatelyRoles);
export type GatelyRole = (typeof gatelyRoles)[number];

/** Roles a syndic may hand-assign when creating an account. `locataire` is
 *  deliberately absent: it is only ever created by occupancy.declare. */
export const assignableGatelyRoles = [
  "syndic",
  "proprietaire",
  "gardien",
] as const;
export const assignableGatelyRoleSchema = z.enum(assignableGatelyRoles);
export type AssignableGatelyRole = (typeof assignableGatelyRoles)[number];

/**
 * Per-résidence permissions a Gately owner grants the syndic. Stored as jsonb
 * on `organization.permissions`. Both default ON so existing résidences keep
 * today's residence-wide visibility until an owner turns a flag off.
 *   - viewAllPasses    — syndic sees the residence-wide Visiteurs list.
 *   - viewAllActivity  — syndic sees any unit's gate-activity feed.
 * Creating/revoking a pass is never governed here — it is always ownership-only.
 */
export const syndicPermissionsSchema = z.object({
  viewAllPasses: z.boolean(),
  viewAllActivity: z.boolean(),
});
export type SyndicPermissions = z.infer<typeof syndicPermissionsSchema>;

export const DEFAULT_SYNDIC_PERMISSIONS: SyndicPermissions = {
  viewAllPasses: true,
  viewAllActivity: true,
};

/** Input for organization.setReglement. When `enabled`, the optional règlement
 * doc is shown on passes and accepted implicitly by entry; `doc=null` or empty clears it. */
export const setReglementInput = z.object({
  doc: richTextDocSchema.nullable(),
  enabled: z.boolean(),
});
export type SetReglementInput = z.infer<typeof setReglementInput>;

/**
 * Guardian (`gardien`) duty state, stored on `member.dutyStatus`. Null for
 * non-guardian members. `on_duty` = "En service", `off_duty` = "Hors service".
 */
export const dutyStatuses = ["on_duty", "off_duty"] as const;
export const dutyStatusSchema = z.enum(dutyStatuses);
export type DutyStatus = (typeof dutyStatuses)[number];

/**
 * Organization lifecycle state used as a commercial lever (e.g. non-payment).
 * `active` = normal. `inactive` = suspended: syndic/proprietaire/gardien are
 * blocked on web + mobile; the Gately `owner` (staff) keeps full access.
 * Distinct from the soft-delete/purge flow (`deletedAt`/`purgeAt`).
 */
export const orgStatuses = ["active", "inactive"] as const;
export const orgStatusSchema = z.enum(orgStatuses);
export type OrgStatus = (typeof orgStatuses)[number];

/**
 * Pure access gate for a suspended organization. Returns true when access
 * must be blocked: the org is inactive AND the caller is not the platform
 * owner. Shared by the API guard (defense in depth) and the web/mobile
 * blocked-screen gate, so all three agree. A null/undefined role (e.g. a
 * shared gate device, which has no member role) is treated as non-owner.
 */
export function isOrgAccessBlocked(
  status: string | null | undefined,
  role: string | null | undefined,
): boolean {
  return status === "inactive" && role !== "owner";
}

/**
 * Reasons a syndic can pick when disabling a proprietaire's account (a manual
 * non-payment lever — Gately does not track payments). Stored as enum keys on
 * `member.disabledReason`; French display labels live in
 * `accountDisableReasonLabels` so web + mobile render the same text.
 */
export const accountDisableReasons = [
  "unpaid_dues",
  "overdue_charges",
  "missing_documents",
  "other",
] as const;
export const accountDisableReasonSchema = z.enum(accountDisableReasons);
export type AccountDisableReason = (typeof accountDisableReasons)[number];

export const accountDisableReasonLabels: Record<AccountDisableReason, string> =
  {
    unpaid_dues: "Cotisations impayées",
    overdue_charges: "Charges en retard",
    missing_documents: "Documents manquants",
    other: "Autre",
  };

/** Input for `account.disable`. `note` is an optional free-text complement to
 * the required picked reason (e.g. an amount or reference). */
export const accountDisableInput = z.object({
  userId: z.string().min(1),
  reason: accountDisableReasonSchema,
  note: z.string().trim().max(500).optional(),
});
export type AccountDisableInput = z.infer<typeof accountDisableInput>;

/**
 * Pure gate for a disabled member: true when the membership has been disabled
 * by a syndic. `disabledAt` is the single source of truth (mirrors how
 * `organization.suspendedAt` pairs with `status`). Shared by the API invite
 * gate and the web/mobile surfaces so all agree.
 */
export function isMemberDisabled(m: {
  disabledAt: Date | string | null | undefined;
}): boolean {
  return m.disabledAt != null;
}

/**
 * Derived account status shown in the web console's STATUT column. Never
 * stored — computed from role + last-sign-in + duty state. Lifecycle
 * (`invited`/`active`) applies to syndic/proprietaire; duty
 * (`on_duty`/`off_duty`) applies to gardien.
 */
export const accountStatuses = [
  "invited",
  "active",
  "on_duty",
  "off_duty",
] as const;
export type AccountStatus = (typeof accountStatuses)[number];

/** Vertical discriminator on organization. Residential is the default. */
export const organizationTypes = ["residence", "office"] as const;
export const organizationTypeSchema = z.enum(organizationTypes);
export type OrganizationType = (typeof organizationTypes)[number];

/** Whether an org records visitor exits, and how.
 *  `manual` = tap-to-close on the présence list + scanner Sortie mode.
 *  `badge`  = `manual` plus a temporary badge code captured at entry.
 *  Badges imply exit tracking, so this is one enum rather than two flags —
 *  the impossible state (badges on, exits off) cannot be spelled. */
export const exitTrackingModes = ["off", "manual", "badge"] as const;
export const exitTrackingSchema = z.enum(exitTrackingModes);
export type ExitTrackingMode = (typeof exitTrackingModes)[number];

/** Vertical default, applied at org creation only. Deliberately NOT read
 *  from `organization.type` at runtime: that column's contract is display
 *  vocabulary, never pass/scan behaviour, and a résidence with a strict gate
 *  must be able to opt in without being mistyped as an office. */
export function defaultExitTracking(
  orgType: OrganizationType,
): ExitTrackingMode {
  return orgType === "office" ? "manual" : "off";
}

/** `organization.setExitTracking` input. */
export const setExitTrackingInput = z.object({
  exitTracking: exitTrackingSchema,
});
export type SetExitTrackingInput = z.infer<typeof setExitTrackingInput>;

/**
 * Which contact identifier is REQUIRED when an admin creates an account, per
 * vertical. Single source of truth shared by the web forms and the API
 * handlers so client validation and server enforcement never disagree.
 *
 *   - residence          → phone (WhatsApp-first; email stays optional)
 *   - office             → email (admins & employés work email-first)
 *   - office + gardien   → phone (the gate app signs in by phone OTP only,
 *                          so an agent d'accueil must always have a phone)
 *
 * The non-required identifier is always accepted as an optional extra.
 */
export function requiredContact(
  orgType: OrganizationType | undefined,
  role?: GatelyRole | undefined,
): "phone" | "email" {
  if (orgType === "office") return role === "gardien" ? "phone" : "email";
  return "phone";
}

/** Optional e-mail form field: trims, treats "" as omitted, validates shape.
 * Shared by every account/member/guest input that carries an email. */
export const optionalEmailSchema = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
  z.string().trim().toLowerCase().email().max(254).optional(),
);

/** Which visitor types each vertical exposes. `locataire` is residential-only. */
export const visitorTypesByOrgType: Record<
  OrganizationType,
  readonly VisitorType[]
> = {
  residence: ["visiteur", "locataire", "intervenant"],
  office: ["visiteur", "intervenant"],
};

/** Company-level roster role on unit_member (distinct from member.role). */
export const unitMemberRoles = ["member", "admin"] as const;
export const unitMemberRoleSchema = z.enum(unitMemberRoles);
export type UnitMemberRole = (typeof unitMemberRoles)[number];

/** « Mettre en location » — the owner declares their tenant. Phone-first like
 *  every residence contact; the phone/email floor mirrors foyer.addMember. */
export const occupancyDeclareInput = z
  .object({
    unitId: z.string().min(1),
    name: z.string().trim().min(1).max(120),
    phone: z.preprocess(
      (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
      phoneSchema.optional(),
    ),
    email: optionalEmailSchema,
  })
  .refine((v) => Boolean(v.phone) || Boolean(v.email), {
    message: "Téléphone ou e-mail requis.",
    path: ["phone"],
  });
export type OccupancyDeclareInput = z.infer<typeof occupancyDeclareInput>;

export const occupancyEndInput = z.object({ unitId: z.string().min(1) });
export type OccupancyEndInput = z.infer<typeof occupancyEndInput>;

/** Invite/pass kinds. Validity semantics:
 * - one_time:   valid a single day (validFrom == start of day, validTo == end of day)
 * - date_range: valid [validFrom, validTo] (the Airbnb stay)
 * - recurring:  valid within [validFrom, validTo?] on `recurrence` weekdays, further
 *               gated to the daily hour window held in `dailyStartMinutes` /
 *               `dailyEndMinutes` — minutes since LOCAL midnight (0-1439), NULL
 *               meaning an absent bound (0 / 1439). Enforced at scan time on the
 *               ORGANIZATION's own zone (`organization.timezone`, an IANA id —
 *               never an offset), read by isOnRecurringSchedule, and failing
 *               open (allow) when `recurrence` doesn't parse into any canonical
 *               weekday (legacy rows).
 *
 *               For a recurring pass, validFrom/validTo bound the overall span
 *               only; their time-of-day carries no meaning and nothing reads
 *               it. The window used to live there, which froze the UTC offset
 *               at creation time and broke at the next transition — GATELY-56
 *               moved it to the wall-clock minute columns, and GATELY-62 made
 *               the zone per-organization instead of a hardcoded
 *               `Africa/Casablanca`.
 *
 * Residents (unit owner + foyer members) no longer use a guest pass for permanent
 * access — they get a derived resident pass instead (see packages/api resident-pass).
 */
export const inviteTypes = ["one_time", "date_range", "recurring"] as const;
export const inviteTypeSchema = z.enum(inviteTypes);
export type InviteType = (typeof inviteTypes)[number];

// `inviteSources` / `inviteSourceSchema` / `InviteSource` live in
// ./invite-search now (re-exported below) — invite-search.ts needs them and
// importing them back from this file would be a load-time cycle.

/**
 * Which invite types a given vertical + visitor type combination exposes.
 *
 * An office "visiteur" is always a single day — a stay or a recurring
 * schedule doesn't make sense for someone dropping by a company for a
 * one-off visit. An office "intervenant" is the opposite case: a contractor
 * doing multi-day work needs to come and go repeatedly, so `date_range`
 * (unlimited re-entry within the stay) and `recurring` (a weekly schedule)
 * both stay available — only `one_time` is truly single-use (see
 * `inviteTypes` doc above), so it wouldn't survive a second day on site.
 */
export function inviteTypesFor(
  orgType: OrganizationType,
  visitorType: VisitorType,
): readonly InviteType[] {
  if (orgType === "office" && visitorType === "visiteur") return ["one_time"];
  return inviteTypes;
}

/**
 * Who the pass is for — a one-off guest (visiteur), a tenant (locataire), or a
 * worker/contractor doing work in the residence or a unit (intervenant).
 */
export const visitorTypes = ["visiteur", "locataire", "intervenant"] as const;
export const visitorTypeSchema = z.enum(visitorTypes);
export type VisitorType = (typeof visitorTypes)[number];

/** Total people allowed on a pass, including the named visitor. 1 = visitor
 *  alone. Empty/omitted = no limit. 50 is a sanity cap. Shared by the invite
 *  create input and the gardien's entry-record input. */
export const headcountSchema = z.number().int().min(1).max(50);

/**
 * Every value ever written to `entry_event.source`.
 *
 * The column is plain text (a new source needs no migration), so this list is
 * the only place the vocabulary is enumerated: the activity filter input, the
 * /activite search schema and the web `SOURCE_LABELS` map all derive from it.
 * `confirm` was once added to the labels alone, which silently killed its
 * filter chip — a shared const is what stops the next source doing the same.
 *
 *   scan     — QR scanned at the gate
 *   manual   — gardien typed the code / recorded by hand
 *   resident — recorded from the résident's app
 *   spot     — arrivée imprévue (`spotArrival`)
 *   confirm  — gardien/manager pressed « Arrivé » on an expected row
 */
export const ENTRY_SOURCES = [
  "scan",
  "manual",
  "resident",
  "spot",
  "confirm",
] as const;
export const entrySourceSchema = z.enum(ENTRY_SOURCES);
export type EntrySource = (typeof ENTRY_SOURCES)[number];

/** Blank strings from a form row mean "not given". */
const blankToUndefined = (v: unknown) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;

/** One guest on an invite (web shape). Phone and email are optional, EXCEPT
 *  that a self-fill guest (`selfFill: true`) needs the email the « Compléter »
 *  link goes to, and a host-filled guest needs a name. On a self-fill guest the
 *  name is only a pre-fill the visitor may correct (visitor self-fill spec
 *  §4.3). Refined in place (zod v4 keeps `.shape` on a refined object), so
 *  `inviteCreateManyInput` can still reuse `inviteGuestInput.shape.name`. */
export const inviteGuestInput = z
  .object({
    name: z.preprocess(
      blankToUndefined,
      z.string().trim().min(1).max(120).optional(),
    ),
    phone: z.preprocess(blankToUndefined, phoneSchema.optional()),
    email: z.preprocess(
      blankToUndefined,
      z.string().trim().email().max(254).optional(),
    ),
    selfFill: z.boolean().optional(),
    /** People allowed on THIS visitor's pass, visitor included (« + N
     *  accompagnants » = N + 1). Omitted = the visitor alone. Spec 2026-09-26 §2. */
    maxHeadcount: headcountSchema.optional(),
    /** This visitor's own answers to the active form. Host-filled visitors only;
     *  a self-fill visitor answers on their own page. */
    formAnswers: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((g) => !g.selfFill || Boolean(g.email), {
    message: "L'e-mail est requis quand le visiteur complète lui-même.",
    path: ["email"],
  })
  .refine((g) => Boolean(g.selfFill) || Boolean(g.name), {
    message: "Le nom est requis.",
    path: ["name"],
  });
export type InviteGuestInput = z.infer<typeof inviteGuestInput>;

export const inviteCreateInput = z
  .object({
    // Optional: omitted = a residence-level pass (no unit). When provided it
    // must be a non-empty id.
    unitId: z.string().min(1).optional(),
    guestName: z.string().trim().min(1).max(120).optional(),
    // Optional. When provided, validate + normalize to canonical E.164 (so the
    // pass-share link / WhatsApp works); empty string is treated as omitted.
    guestPhone: z.preprocess(
      (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
      phoneSchema.optional(),
    ),
    guests: z.array(inviteGuestInput).min(1).max(20).optional(),
    locationId: z.string().min(1).optional(),
    /** Invite visitors to a confirmed amenity booking. When set the SERVER
     *  owns the window (the booking's, opening 30 min early), the pass type
     *  (one_time), the unit (the booking's) and the meeting point (the booked
     *  room): whatever the client sent for those is overridden. */
    bookingId: z.string().min(1).optional(),
    type: inviteTypeSchema,
    visitorType: visitorTypeSchema.default("visiteur"),
    /** The authored validity window, as either shape `authoredInstant`
     *  accepts (kept a plain string for zod-v4 portability — no
     *  `.datetime()`):
     *
     *  - a zoneless WALL CLOCK, "2026-09-22T13:15" — what clients send. The
     *    digits the picker showed; the server anchors them in the org's zone.
     *  - an ISO INSTANT, "…Z" — what builds in the wild still send, taken at
     *    face value.
     *
     *  Clients must not convert a wall clock to an instant themselves: their
     *  IANA table is whatever their browser or phone last shipped, and when it
     *  disagrees with the server's the pass is stored at the wrong hour
     *  (GATELY-40 Level 2). */
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    /** Comma-separated weekday codes for recurring, e.g. "mon,tue". Free-form for now. */
    recurrence: z.string().trim().max(64).optional(),
    /** Recurring daily access window, minutes since local midnight in the
     *  organization's timezone. Independent of validFrom/validTo, which is
     *  what lets a recurring pass have hours without an end date. */
    dailyStartMinutes: z.number().int().min(0).max(1439).optional(),
    dailyEndMinutes: z.number().int().min(0).max(1439).optional(),
    note: z.string().trim().max(280).optional(),
    /** LEGACY (mobile, one guest): the cap for that guest. Several guests carry
     *  their own `guests[].maxHeadcount` — refused here, see the refine below. */
    maxHeadcount: headcountSchema.optional(),
    /** Answers to the residence's active form for this visitor type. Validated
     * server-side against the form definition; shape is field-key → value. */
    formAnswers: z.record(z.string(), z.unknown()).optional(),
  })
  .refine(
    (v) =>
      v.type !== "date_range" || (Boolean(v.validFrom) && Boolean(v.validTo)),
    {
      message:
        "Un accès sur plusieurs jours nécessite une date de début et de fin.",
    },
  )
  .refine((v) => v.type !== "recurring" || Boolean(v.recurrence), {
    message: "Un accès récurrent nécessite des jours.",
  })
  // The end of a validity window must come after its start. Without this a
  // same-day pass with an end time earlier than its start time (e.g.
  // 21:06 → 12:06) is stored with validTo < validFrom and reads as expired
  // the moment it's created.
  //
  // Checkable here only when both bounds arrive in the SAME shape: two wall
  // clocks compare correctly whatever zone the runtime parses them in (one
  // offset, applied to both), and two instants are absolute. A mixed pair
  // cannot be compared at all without the organization's zone, which this
  // schema has never had, so it passes through to `authoredWindow` — which
  // holds that zone and rejects an inversion there.
  .refine(
    (v) =>
      !v.validFrom ||
      !v.validTo ||
      isWallClock(v.validFrom) !== isWallClock(v.validTo) ||
      new Date(v.validTo).getTime() > new Date(v.validFrom).getTime(),
    {
      message: "La fin de validité doit être après le début.",
      path: ["validTo"],
    },
  )
  .refine((v) => (v.guests && v.guests.length > 0) || Boolean(v.guestName), {
    message: "Au moins un invité est requis.",
    path: ["guests"],
  })
  // One number cannot describe several visitors: it used to be copied onto
  // every guest, multiplying the desk's head count (audit 2026-09-26 P0-1).
  .refine((v) => v.maxHeadcount === undefined || (v.guests?.length ?? 1) <= 1, {
    message: "Indiquez les accompagnants sur chaque visiteur.",
    path: ["maxHeadcount"],
  })
  // Both bounds or neither — a lone `dailyStartMinutes` (or vice versa) is
  // the shape a half-finished form submission takes, not a deliberate
  // "until midnight"/"since midnight" choice. The UI presents them as one
  // pair with no affordance saying blank means an open end, so silently
  // defaulting the missing bound to 0/1439 would let the created pass
  // enforce hours the form never showed the caller.
  .refine(
    (v) =>
      (v.dailyStartMinutes === undefined) === (v.dailyEndMinutes === undefined),
    {
      message: "Renseignez les deux heures d'accès, ou aucune des deux.",
      path: ["dailyEndMinutes"],
    },
  )
  // A zero-length window admits nobody. end < start is NOT an error — that's
  // an overnight window (20:00 -> 06:00), which isOnRecurringSchedule handles.
  .refine(
    (v) =>
      v.dailyStartMinutes === undefined ||
      v.dailyEndMinutes === undefined ||
      v.dailyStartMinutes !== v.dailyEndMinutes,
    {
      message: "Les heures d'accès doivent couvrir une plage non vide.",
      path: ["dailyEndMinutes"],
    },
  );
export type InviteCreateInput = z.infer<typeof inviteCreateInput>;

// ─── Invitation import (Excel/CSV) ───────────────────────────────────────────
// Spec 2026-08-27-invite-import-design §4. Declared here rather than in a
// module of their own because they reference `inviteGuestInput.shape`, and a
// separate file re-exported by this index would be a circular import.

/** One spreadsheet column → one invitation field. `ignore` = column unused. */
export const importFields = [
  "name",
  "phone",
  "email",
  "date",
  "start",
  "end",
  "note",
  "companions",
  "ignore",
] as const;
export const importFieldSchema = z.enum(importFields);
export type ImportField = z.infer<typeof importFieldSchema>;

/**
 * `invite.draftFromTable` input: a raw grid produced by the browser parser.
 * No file is ever uploaded. Caps mirror `apps/web/src/lib/invite-import.ts`,
 * which refuses to send anything larger, so hitting these means a client that
 * skipped its own check.
 */
export const draftTableInput = z.object({
  headers: z.array(z.string().max(120)).min(1).max(30),
  rows: z
    .array(z.array(z.string().max(500)))
    .min(1)
    .max(200),
  /** One entry per header. Absent = ask the model to propose one. */
  mapping: z.array(importFieldSchema).optional(),
});
export type DraftTableInput = z.infer<typeof draftTableInput>;

/**
 * `invite.createMany` input. One row = one invitation with its own window,
 * of whatever pass type `shared.type` says (one_time / date_range /
 * recurring — 2026-09-13 spec §7); `shared` carries what every row has in
 * common. Form answers and headcount are per row — never shared. For a
 * `one_time` row, `validTo` omitted = the organization's day-end applies
 * (resolveOneTimeWindow, slice 1) — see `rows[].validFrom` below for what
 * omitted means for the other two pass types.
 */
export const inviteCreateManyInput = z.object({
  shared: z.object({
    unitId: z.string().min(1).optional(),
    locationId: z.string().min(1).optional(),
    visitorType: visitorTypeSchema.default("visiteur"),
    note: z.string().trim().max(280).optional(),
    /** Refused: answers belong to one person. Copying them into N invitations
     *  put one visitor's CIN in everyone's dossier (audit 2026-09-26 P0-2). */
    formAnswers: z
      .never({ error: "Les réponses au formulaire se donnent par visiteur." })
      .optional(),
    /** Never accepted: a booking-linked invite takes its window from the
     *  booking, which only `invite.create` enforces. Refused outright rather
     *  than stripped, so a client that sends one learns it at once instead of
     *  getting passes that silently ignore the booking. */
    bookingId: z
      .never({ error: "Les invitations séparées ne peuvent pas être liées à une réservation." })
      .optional(),
    // Mirrors inviteCreateInput's window fields so a batch is no longer
    // one_time-only (2026-09-13 spec §7), and carries the same convention:
    // a zoneless wall clock from a client, or a legacy instant, resolved by
    // `authoredWindow` per row.
    type: inviteTypeSchema.default("one_time"),
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    recurrence: z.string().trim().max(64).optional(),
    dailyStartMinutes: z.number().int().min(0).max(1439).optional(),
    dailyEndMinutes: z.number().int().min(0).max(1439).optional(),
  }),
  rows: z
    .array(
      z.object({
        name: inviteGuestInput.shape.name,
        /** Email-only import rows arrive as `selfFill: true` (spec §4.1).
         *  The per-row `inviteCreateInput.safeParse` in `createMany` runs
         *  `inviteGuestInput`'s refines, so a self-fill row without an email
         *  becomes a failed row with the refine's French message. */
        selfFill: z.boolean().optional(),
        /** Raw, NOT normalized: `createMany` validates contacts per row inside
         *  its loop, so one unparseable phone becomes a failed row instead of
         *  400-ing the whole batch (spec §4.4). `inviteGuestInput.shape.phone`
         *  cannot be used here — it fails the entire request. */
        phone: z.string().trim().max(32).optional(),
        email: z.string().trim().max(254).optional(),
        /** An authored wall clock (or a legacy instant), like
         *  `inviteCreateInput.validFrom`. OPTIONAL since the 2026-09-13
         *  spec: a row with no
         *  date inherits `shared.validFrom` (`resolveBatchRowWindow`). A row
         *  resolvable from NEITHER is passed through to `createInvite` as
         *  `undefined` — for a `one_time` row that is not a failure at all,
         *  it resolves to the org's day-end default, exactly as a bare
         *  `invite.create` call does. `date_range` and `recurring` rows have
         *  no such default: `inviteCreateInput`'s own refine rejects those
         *  when the window is still missing, which becomes a failed row
         *  (not a 400) further down `createMany`'s per-row loop. */
        validFrom: z.string().min(1).optional(),
        validTo: z.string().min(1).optional(),
        /** Overrides `shared.note` for this row. */
        note: z.string().trim().max(280).optional(),
        /** This visitor's cap, as `inviteGuestInput.maxHeadcount`. */
        maxHeadcount: headcountSchema.optional(),
        /** This visitor's answers, as `inviteGuestInput.formAnswers`. */
        formAnswers: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .min(1)
    .max(200),
});
export type InviteCreateManyInput = z.infer<typeof inviteCreateManyInput>;

// ─── Visitor self-fill (spec 2026-09-23 §5.2) ────────────────────────────────

/** The self-fill link's only credential: the guest's pass token. */
export const selfFillTokenInput = z.object({
  token: z.string().min(1).max(64),
});

/** `pass.selfFill.uploadUrl`. `form.uploadUrl`'s names and limits, plus the
 *  token. No `documentKind`: the server reads it off the form field, a public
 *  caller does not get to classify its own upload. */
export const selfFillUploadUrlInput = formUploadUrlInput
  .omit({ documentKind: true })
  .extend({ token: selfFillTokenInput.shape.token });
export type SelfFillUploadUrlInput = z.infer<typeof selfFillUploadUrlInput>;

/** `pass.selfFill.submit`. The email is NOT here: it is the one the host
 *  typed, read-only on the page, and the server uses the stored value. */
export const selfFillSubmitInput = z.object({
  token: selfFillTokenInput.shape.token,
  identity: z.object({
    name: z.string().trim().min(1, "Indiquez votre nom.").max(120),
    phone: z.preprocess(blankToUndefined, phoneSchema.optional()),
  }),
  answers: z.record(z.string(), z.unknown()).default({}),
  consent: z.literal(true, { error: "Vous devez accepter pour continuer." }),
});
export type SelfFillSubmitInput = z.infer<typeof selfFillSubmitInput>;

/** `invite.resendSelfFill`. */
export const resendSelfFillInput = z.object({ guestId: z.string().min(1) });

/** `invite.completeGuestForm`: the host fills the form for a visitor still
 *  awaiting their own link. `name` only when the host never named them. */
export const completeGuestFormInput = z.object({
  guestId: z.string().min(1),
  name: z.string().trim().min(1).max(200).optional(),
  formAnswers: z.record(z.string(), z.unknown()),
});
export type CompleteGuestFormInput = z.infer<typeof completeGuestFormInput>;

/** One guest as `createInvite` handles it. `name` is null only for a
 *  self-fill guest the host did not name (`inviteGuestInput` refines). */
export interface NormalizedInviteGuest {
  name: string | null;
  phone: string | null;
  email: string | null;
  selfFill: boolean;
  maxHeadcount: number | null;
  formAnswers: Record<string, unknown> | null;
}

/** Funnels both wire shapes (web `guests[]`, mobile legacy guestName/guestPhone)
 *  into one normalized guest list. Callers may assume length ≥ 1 after
 *  inviteCreateInput validation. */
export function normalizeInviteGuests(input: {
  guests?: InviteGuestInput[];
  guestName?: string;
  guestPhone?: string;
}): NormalizedInviteGuest[] {
  if (input.guests && input.guests.length > 0) {
    return input.guests.map((g) => ({
      name: g.name?.trim() ?? null,
      phone: g.phone ?? null,
      email: g.email ?? null,
      selfFill: g.selfFill === true,
      maxHeadcount: g.maxHeadcount ?? null,
      formAnswers: g.formAnswers ?? null,
    }));
  }
  if (input.guestName) {
    return [
      {
        name: input.guestName,
        phone: input.guestPhone ?? null,
        email: null,
        selfFill: false,
        maxHeadcount: null,
        formAnswers: null,
      },
    ];
  }
  return [];
}

/** Hard cap on a single paste. Rejected client- and server-side before any
 *  provider call, so a pasted novel costs nothing. */
export const MAX_PASTE_CHARS = 50_000;

export const draftTextInput = z.object({
  text: z.string().trim().min(1).max(MAX_PASTE_CHARS),
});

export const draftWarningCodes = [
  "unnamed_extra",
  "unreadable_phone",
  "vague_time",
  "truncated_name",
  "dropped_guest",
  "over_cap",
  /** The extraction succeeded but found nobody — surfaced by the client. */
  "no_guests",
] as const;
export type DraftWarningCode = (typeof draftWarningCodes)[number];

/**
 * The subset the PROMPT authorizes the model to emit. The other four codes are
 * constructed by `normalizeDraft` from what it actually did, so accepting them
 * from model output would let a crafted paste fabricate e.g. an `over_cap`
 * claiming guests were dropped. Parsing path only — code-built warnings are
 * unaffected.
 */
export const modelDraftWarningCodes = ["unnamed_extra", "vague_time"] as const;

/** Ceiling on model-authored warning text, which renders in the amber banner. */
export const MAX_WARNING_DETAIL = 200;

export interface DraftWarning {
  code: DraftWarningCode;
  /** French, user-facing. */
  detail: string;
  /** Index into the returned guests array, for per-row highlighting. */
  guestIndex?: number;
}

const nullableStr = z
  .string()
  .nullish()
  .transform((v) => v ?? null);

/**
 * The MODEL's raw output shape. Deliberately lenient: DeepSeek supports only
 * loose JSON mode (no schema constraint), so this schema's job is to survive
 * plausible variation, not to enforce business rules. Business rules live in
 * `normalizeDraft` (packages/api/src/lib/invite-draft.ts).
 */
export const inviteDraftSchema = z.object({
  guests: z
    .array(
      z.object({
        name: z
          .string()
          .nullish()
          .transform((v) => v ?? ""),
        phone: nullableStr,
        email: nullableStr,
      }),
    )
    .min(0),
  visitorType: nullableStr,
  type: nullableStr,
  validFrom: nullableStr,
  validTo: nullableStr,
  warnings: z
    .array(z.unknown())
    .nullish()
    .transform((arr) =>
      (arr ?? []).flatMap((w) => {
        if (typeof w !== "object" || w === null) return [];
        const rec = w as Record<string, unknown>;
        const code = rec.code;
        if (typeof code !== "string") return [];
        if (!(modelDraftWarningCodes as readonly string[]).includes(code))
          return [];
        return [
          {
            code: code as DraftWarningCode,
            detail:
              typeof rec.detail === "string"
                ? rec.detail.slice(0, MAX_WARNING_DETAIL)
                : "",
          },
        ];
      }),
    ),
});
export type RawInviteDraft = z.infer<typeof inviteDraftSchema>;

/** The normalized draft handed to the client. */
export interface InviteDraft {
  guests: {
    name: string;
    /** Local-format digits for the form's PhoneInput. */
    phone?: string;
    country?: CountryCode;
    email?: string;
  }[];
  visitorType: VisitorType | null;
  type: InviteType | null;
  /** A wall clock on the ORGANIZATION's clock ("2026-09-03T09:00"), never an
   *  instant: the form shows these digits and submits them back unchanged, so
   *  no client ever has to know what zone they are read in (GATELY-40 Level
   *  2). `normalizeDraft` re-reads a model that answered with an offset. */
  validFrom: string | null;
  validTo: string | null;
}
